import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useChat } from "@/features/chat";
import {
  advisorsList,
  AdvisorAdsCard,
  type ZodiacSign,
} from "@/features/advisors";

import Decoration from "@/assets/img/today/horoscope/signs_huge_decoration.webp";

import { ZODIAC_ART } from "../../config/horoscope-signs";
import { buildHoroscopePredictions } from "../../config/horoscope-content";
import { useAstroToday } from "../../hooks/use-astro-today";
import { TodayLoader } from "../today-loader";
import { PersonalizedReports } from "../personalized-reports";
import { ADVISOR_PRESETS } from "../../config/advisor-presets";
import { FeaturesSlider } from "./features-slider";
import { Prediction } from "./prediction";
import { QuietSigns } from "./quiet-signs";
import { HoroscopeCountdown } from "./horoscope-countdown";

const normalizeSign = (sign: string | undefined): ZodiacSign | null => {
  if (!sign) return null;
  const capitalized = (sign.charAt(0).toUpperCase() +
    sign.slice(1).toLowerCase()) as ZodiacSign;
  return ZODIAC_ART[capitalized] ? capitalized : null;
};

function toNonEmptyText(value: string | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeTags(tags: string[] | undefined): string[] {
  return (tags ?? []).map((tag) => tag.trim()).filter((tag) => tag.length > 0);
}

/** Horoscope category — the user's daily/monthly forecast from the backend. Ported from Today/Horoscope. */
export function HoroscopeCategory() {
  const navigate = useNavigate();
  const { setSelectedAdvisor, setPreSelectedChatMessage } = useChat();
  const { data, meta, loading } = useAstroToday(["daily_horoscope"]);
  const dailyHoroscope = data?.daily_horoscope;

  const sign = normalizeSign(dailyHoroscope?.sign);
  const title = toNonEmptyText(dailyHoroscope?.title);
  const intro = toNonEmptyText(dailyHoroscope?.intro);
  const focusTags = useMemo(
    () => normalizeTags(dailyHoroscope?.focus_tags),
    [dailyHoroscope?.focus_tags],
  );
  const troublesTags = useMemo(
    () => normalizeTags(dailyHoroscope?.troubles_tags),
    [dailyHoroscope?.troubles_tags],
  );
  const predictions = useMemo(
    () => buildHoroscopePredictions(dailyHoroscope),
    [dailyHoroscope],
  );
  const advisorMessage = intro;

  const askAdvisor = (preset?: string) => {
    if (preset) setPreSelectedChatMessage(preset);
    setSelectedAdvisor(advisorsList[1]);
    navigate({ to: WebappRoutes.READINGS });
  };

  if (loading) return <TodayLoader />;
  if (
    !dailyHoroscope ||
    !sign ||
    !title ||
    !intro ||
    !predictions ||
    focusTags.length === 0 ||
    troublesTags.length === 0
  ) {
    return <TodayLoader empty />;
  }

  return (
    <div className="w-full">
      {/* Hero */}
      <div className="relative flex items-center justify-center">
        <img src={Decoration} alt="" className="select-none" />
        <img
          src={ZODIAC_ART[sign].huge}
          alt={sign}
          className="absolute w-[57.7%] rounded-full"
        />
      </div>

      <h2 className="mx-4 mt-2 font-display text-2xl font-medium text-purple-light">
        {title}
      </h2>

      <p className="mx-4 mt-3 font-body text-base text-purple-light/70">
        {intro}
      </p>

      <FeaturesSlider focusTags={focusTags} troublesTags={troublesTags} />

      <Prediction prediction={predictions[0]} />
      <Prediction prediction={predictions[1]} />

      {/* Chat with advisor */}
      <div className="px-4">
        <h3 className="mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
          Need clarity from the stars?
        </h3>
        <p className="mb-6 font-body text-base text-purple-light/70">
          Let an astrologer help you understand today’s signs and how they
          affect your next move.
        </p>
        {advisorMessage && (
          <AdvisorAdsCard
            advisor={advisorsList[1]}
            message={advisorMessage}
            presets={[...ADVISOR_PRESETS.horoscope]}
            onPresetClick={askAdvisor}
          />
        )}
      </div>

      <Prediction prediction={predictions[2]} />
      <Prediction prediction={predictions[3]} />

      <QuietSigns onAsk={askAdvisor} />

      <PersonalizedReports />

      <HoroscopeCountdown target={meta?.next_sync} />
    </div>
  );
}
